import {
  Button,
  DatePicker,
  InputNumber,
  message,
  Modal,
  Select,
  Switch,
} from "antd";
import React, { useEffect, useState } from "react";
import {
  useCreateInputPrice,
  useGetInputPriceById,
  useUpdateInputPrice,
} from "../../api/admin";
import { useInputList } from "../../api/input";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { InputPriceEnum } from "../../enums/InputPrice.enum";
import { useOrganizationsList } from "../../api/organization";
import { buildSearchableDropdownRender } from "../ui/searchable-select-dropdown";

type InputPriceModalProps = {
  open: boolean;
  selectedId?: number;
  hideModal: () => void;
};

// Create a dynamic schema that depends on isMerchant state
const createInputPriceSchema = (isMerchant: boolean) =>
  z.object({
    selectedUnionId: z.string().optional(),
    selectedCooperativeId: isMerchant
      ? z.string().optional()
      : z.string().min(1, { message: "Cooperative is required" }),
    organizationId: isMerchant
      ? z.string().min(1, { message: "Merchant is required" })
      : z.string().optional(),
    selectedInputId: z.string().min(1, { message: "Product is required" }),
    effectiveDate: z.string().min(1, { message: "Effective date is required" }),
    inputPrice: z.preprocess(
      (val) =>
        val === undefined || val === null || val === ""
          ? undefined
          : Number(val),
      z
        .number()
        .refine((val) => val > 0, { message: "Price must be greater than 0" }),
    ),
  });

type InputPriceForm = z.infer<ReturnType<typeof createInputPriceSchema>>;

// Custom hook for searchable select
const useSearchableSelect = (
  type: "MERCHANT" | "MULTI_PURPOSE_COOPERATIVE",
  initialSearch = "",
) => {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, isLoading, isFetching } = useOrganizationsList({
    status: "ACTIVE",
    merchantType: type,
    page,
    size: 10,
    search: searchTerm,
  });

  // Reset pagination and options when search changes
  useEffect(() => {
    setPage(0);
    setAllOptions([]);
    setHasMore(true);
  }, [searchTerm]);

  // Update options when new data arrives
  useEffect(() => {
    if (data?.data?.content) {
      const newOptions = data.data.content.map((item: any) => ({
        label: item.name,
        value: String(item.id),
      }));

      if (page === 0) {
        setAllOptions(newOptions);
      } else {
        setAllOptions((prev) => [...prev, ...newOptions]);
      }

      // Check if there are more pages
      setHasMore(!data.data.last);
    }
  }, [data, page]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleLoadMore = async () => {
    if (hasMore && !isFetching && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
      setIsLoadingMore(false);
    }
  };

  return {
    options: allOptions,
    isLoading: isLoading || isFetching,
    isLoadingMore,
    hasMore,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    searchTerm,
  };
};

const useProductSearchableSelect = (initialSearch = "") => {
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const [allItems, setAllItems] = useState<any[]>([]); // Store full items
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { data, isLoading, isFetching } = useInputList({
    status: "ACTIVE",
    page,
    size: 10,
    search: searchTerm,
  });

  // Reset pagination and options when search changes
  useEffect(() => {
    setPage(0);
    setAllOptions([]);
    setAllItems([]);
    setHasMore(true);
  }, [searchTerm]);

  // Update options when new data arrives
  useEffect(() => {
    if (data?.data?.content) {
      const newItems = data.data.content;
      const newOptions = newItems.map((item: any) => ({
        label: item.unitOfMeasurementName
          ? `${item.name} (${item.unitOfMeasurementName})`
          : item.name,
        value: String(item.id),
        data: item,
      }));

      if (page === 0) {
        setAllOptions(newOptions);
        setAllItems(newItems);
      } else {
        setAllOptions((prev) => [...prev, ...newOptions]);
        setAllItems((prev) => [...prev, ...newItems]);
      }

      // Check if there are more pages
      setHasMore(!data.data.last);
    }
  }, [data, page]);

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleLoadMore = async () => {
    if (hasMore && !isFetching && !isLoadingMore) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
      setIsLoadingMore(false);
    }
  };

  // Helper function to find item by ID
  const findItemById = (id: string) => {
    return allItems.find((item) => String(item.id) === id);
  };

  return {
    options: allOptions,
    items: allItems, // Export items for access
    findItemById, // Export helper function
    isLoading: isLoading || isFetching,
    isLoadingMore,
    hasMore,
    onSearch: handleSearch,
    onLoadMore: handleLoadMore,
    searchTerm,
  };
};

const InputPriceModal: React.FC<InputPriceModalProps> = ({
  open,
  selectedId,
  hideModal,
}) => {
  const { data: selectedInputPrice, refetch } =
    useGetInputPriceById(selectedId);
  const { mutateAsync: createInputPrice, isPending: creating } =
    useCreateInputPrice();
  const { mutateAsync: updateInputPrice, isPending: updating } =
    useUpdateInputPrice();
  const [isMerchant, setIsMerchant] = useState(false);

  // Use custom hooks for searchable selects
  const {
    options: mpcOptions,
    isLoading: isLoadingMpc,
    isLoadingMore: isLoadingMoreMpc,
    hasMore: hasMoreMpc,
    onSearch: onMpcSearch,
    onLoadMore: onMpcLoadMore,
  } = useSearchableSelect("MULTI_PURPOSE_COOPERATIVE");

  const {
    options: merchantOptions,
    isLoading: isLoadingMerchant,
    isLoadingMore: isLoadingMoreMerchant,
    hasMore: hasMoreMerchant,
    onSearch: onMerchantSearch,
    onLoadMore: onMerchantLoadMore,
  } = useSearchableSelect("MERCHANT");

  // For input product with full data support
  const {
    options: productOptions,
    findItemById,
    isLoading: isLoadingOutput,
    isLoadingMore: isLoadingMoreOutput,
    hasMore: hasMoreOutput,
    onSearch: onOutputSearch,
    onLoadMore: onOutputLoadMore,
  } = useProductSearchableSelect();

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<InputPriceForm>({
    resolver: zodResolver(createInputPriceSchema(isMerchant)) as any,
    defaultValues: {
      selectedUnionId: undefined,
      selectedCooperativeId: undefined,
      organizationId: undefined,
      selectedInputId: undefined,
      effectiveDate: undefined,
      inputPrice: 0,
    },
    mode: "onChange",
  });

  // Re-trigger validation when isMerchant changes
  useEffect(() => {
    trigger();
  }, [isMerchant, trigger]);

  // Handle merchant/union switch change
  const handleMerchantSwitchChange = (checked: boolean) => {
    setIsMerchant(checked);

    // Reset form fields when switching between merchant and union
    reset({
      selectedInputId: getValues("selectedInputId"),
      effectiveDate: getValues("effectiveDate"),
      inputPrice: getValues("inputPrice"),
    });
  };

  // Populate form when editing
  useEffect(() => {
    if (selectedId) refetch();
    else reset();
  }, [selectedId, refetch, reset]);

  // Populate form values when editing
  useEffect(() => {
    if (selectedInputPrice?.data?.data) {
      const price = selectedInputPrice.data.data;
      setValue("selectedInputId", String(price.input?.id));
      setValue("selectedUnionId", String(price.warehouse?.union?.id));
      setValue("selectedCooperativeId", String(price.organization?.orgName));
      setValue(
        "effectiveDate",
        new Date(price.effectiveDate).toLocaleDateString(),
      );
      setValue("inputPrice", price.inputPrice);

      // You might need to adjust this logic based on your data structure
      if (price.type === InputPriceEnum.Merchant) {
        // Merchant-specific
        setValue("organizationId", String(price.organizationId || ""));
        setValue("selectedUnionId", "");
        setValue("selectedCooperativeId", String(price.organization?.orgName));
        setIsMerchant(true);
      } else {
        // Union/Cooperative-specific
        const unionId = String(price.multiPurposeCooperative?.union?.id || "");
        const coopId = String(price.multiPurposeCooperative?.id || "");
        setValue("selectedUnionId", unionId);
        setValue("selectedCooperativeId", coopId);
        setValue("organizationId", String(price.organizationId || ""));
        setIsMerchant(false);
      }
    }
  }, [selectedInputPrice, setValue]);

  const onSubmit: SubmitHandler<InputPriceForm> = async (data) => {
    try {
      const effDate = data.effectiveDate
        ? new Date(data.effectiveDate).getTime()
        : 0;

      // Use the findItemById function to get the full input data
      const inputItem = findItemById(data.selectedInputId);

      console.log("Data", data);
      console.log("Found input item", inputItem);

      if (!inputItem) {
        message.error("Invalid input selection");
        return;
      }

      // Handle merchant case
      if (isMerchant) {
        if (!data.organizationId) {
          message.error("Merchant is required");
          return;
        }

        const payload = {
          id: selectedId ?? 0,
          input: {
            id: inputItem.id,
            inputName: inputItem.name,
            description: inputItem.description ?? "",
            unitOfMeasurementId: inputItem.unitOfMeasurementId,
            status: inputItem.status ?? "ACTIVE",
          },
          organizationId: data.organizationId,
          mpcId: null,
          inputPrice: data.inputPrice,
          effectiveDate: effDate,
          status: "ACTIVE",
          agroDealer: null,
          type: InputPriceEnum.Merchant,
        };

        console.log("Merchant Payload", payload);

        if (selectedId) {
          await updateInputPrice(payload);
          message.success("Merchant price updated successfully");
        } else {
          await createInputPrice(payload);
          message.success("Merchant price created successfully");
        }
      }
      // Handle union/cooperative case
      else {
        // Get the organization ID from the cooperative selection
        const organizationId = data.selectedCooperativeId;

        if (!organizationId) {
          message.error("Cooperative is required");
          return;
        }

        const payload = {
          id: selectedId ?? 0,
          input: {
            id: inputItem.id,
            inputName: inputItem.name,
            description: inputItem.description ?? "",
            unitOfMeasurementId: inputItem.unitOfMeasurementId,
            status: inputItem.status ?? "ACTIVE",
          },
          organizationId: organizationId,
          mpcId: organizationId,
          inputPrice: data.inputPrice,
          effectiveDate: effDate,
          status: "ACTIVE",
          agroDealer: null,
          type: InputPriceEnum.Mpc,
        };

        console.log("Union Payload", payload);

        if (selectedId) {
          await updateInputPrice(payload);
          message.success("MPC price updated successfully");
        } else {
          await createInputPrice(payload);
          message.success("MPC price created successfully");
        }
      }

      hideModal();
      reset();
      setIsMerchant(false);
    } catch (err) {
      console.error(err);
      message.error("Operation failed");
    }
  };

  const resetForm = () => {
    reset();
    setIsMerchant(false);
  };

  // Custom dropdown render for search and infinite scroll
  const renderDropdown =
    (type: "merchant" | "mpc") => (menu: React.ReactElement) => {
      const options = type === "merchant" ? merchantOptions : mpcOptions;
      const isLoading = type === "merchant" ? isLoadingMerchant : isLoadingMpc;
      const isLoadingMore =
        type === "merchant" ? isLoadingMoreMerchant : isLoadingMoreMpc;
      const hasMore = type === "merchant" ? hasMoreMerchant : hasMoreMpc;
      const onSearch = type === "merchant" ? onMerchantSearch : onMpcSearch;
      const onLoadMore =
        type === "merchant" ? onMerchantLoadMore : onMpcLoadMore;

      return buildSearchableDropdownRender({
        searchPlaceholder: `Search ${
          type === "merchant" ? "merchants" : "cooperatives"
        }...`,
        onSearch,
        isLoading,
        isLoadingMore,
        hasMore,
        onLoadMore,
        optionCount: options.length,
      })(menu);
    };

  const renderOutputProductDropdown = () => (menu: React.ReactElement) =>
    buildSearchableDropdownRender({
      searchPlaceholder: "Search products...",
      onSearch: onOutputSearch,
      isLoading: isLoadingOutput,
      isLoadingMore: isLoadingMoreOutput,
      hasMore: hasMoreOutput,
      onLoadMore: onOutputLoadMore,
      optionCount: productOptions.length,
    })(menu);

  return (
    <Modal
      afterClose={resetForm}
      destroyOnClose
      title={
        <div className="flex flex-col items-center justify-center font-semibold text-black text-[27.43px]">
          {selectedId !== undefined ? "Edit Input Price" : "Create Input Price"}
        </div>
      }
      open={open}
      centered
      onOk={handleSubmit(onSubmit)}
      loading={creating || updating}
      onCancel={hideModal}
      closable
      footer={
        <div className="flex justify-end gap-3 p-4">
          <Button
            onClick={hideModal}
            className="!border-gray-300 !text-gray-700 font-medium hover:!bg-gray-100 hover:!text-gray-900 transition-all duration-200 bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit(onSubmit)}
            className="!bg-[#009602] !border-none hover:!bg-[#00b306] font-medium transition-all duration-200"
            loading={creating || updating}
            disabled={creating || updating}
          >
            {selectedId ? "Update" : "Create"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {/* Merchant/Union Switch */}
        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
          <span className="font-medium text-gray-700">
            {isMerchant ? "Merchant" : "Union/Cooperative"}
          </span>
          <Switch
            checked={isMerchant}
            onChange={handleMerchantSwitchChange}
            checkedChildren="Merchant"
            unCheckedChildren="Union"
            className={isMerchant ? "!bg-blue-600" : "!bg-gray-400"}
          />
        </div>

        {/* Merchant Select - Only show when isMerchant is true */}
        {isMerchant && (
          <Controller
            name="organizationId"
            control={control}
            rules={{ required: isMerchant ? "Merchant is required" : false }}
            render={({ field }) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Merchant *
                </label>
                <Select
                  {...field}
                  placeholder="Select Merchant"
                  value={field.value || undefined}
                  style={{ width: "100%" }}
                  status={errors.organizationId ? "error" : undefined}
                  options={merchantOptions}
                  onChange={(val) => {
                    field.onChange(val);
                  }}
                  dropdownRender={renderDropdown("merchant")}
                  filterOption={false}
                  showSearch={false}
                />
                {errors.organizationId && (
                  <span style={{ color: "red" }}>
                    {errors.organizationId.message}
                  </span>
                )}
              </div>
            )}
          />
        )}

        {/* Union and Cooperative Select - Only show when isMerchant is false */}
        {!isMerchant && (
          <>
            {/* Cooperative Select */}
            <Controller
              name="selectedCooperativeId"
              control={control}
              rules={{
                required: !isMerchant ? "Cooperative is required" : false,
              }}
              render={({ field }) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cooperative *
                  </label>
                  <Select
                    {...field}
                    placeholder="Select Cooperative"
                    value={field.value || undefined}
                    style={{ width: "100%" }}
                    options={mpcOptions}
                    onChange={(val) => field.onChange(val)}
                    dropdownRender={renderDropdown("mpc")}
                    filterOption={false}
                    showSearch={false}
                  />
                  {errors.selectedCooperativeId && (
                    <span style={{ color: "red" }}>
                      {errors.selectedCooperativeId.message}
                    </span>
                  )}
                </div>
              )}
            />
          </>
        )}

        {/* Product Select */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Product *
          </label>
          <Controller
            name="selectedInputId"
            control={control}
            render={({ field }) => (
              <Select
                {...field}
                placeholder="Select product"
                value={field.value || undefined}
                style={{ width: "100%" }}
                status={errors.selectedInputId ? "error" : undefined}
                options={productOptions}
                onChange={(val) => field.onChange(val)}
                dropdownRender={renderOutputProductDropdown()}
                filterOption={false}
                showSearch={false}
              />
            )}
          />
          {errors.selectedInputId && (
            <span style={{ color: "red" }}>
              {errors.selectedInputId.message}
            </span>
          )}
        </div>

        {/* Effective Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Effective Date *
          </label>
          <Controller
            name="effectiveDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                style={{ width: "100%" }}
                value={field.value ? dayjs(field.value) : undefined}
                status={errors.effectiveDate ? "error" : undefined}
                onChange={(_, dateString) => field.onChange(dateString)}
                disabledDate={(current) =>
                  current && current < dayjs().startOf("day")
                }
              />
            )}
          />
          {errors.effectiveDate && (
            <span style={{ color: "red" }}>{errors.effectiveDate.message}</span>
          )}
        </div>

        {/* Input Price */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Input Price *
          </label>
          <Controller
            name="inputPrice"
            control={control}
            render={({ field }) => (
              <InputNumber
                className="w-full"
                placeholder="Insert input price"
                value={field.value || undefined}
                status={errors.inputPrice ? "error" : undefined}
                onChange={(val) => field.onChange(val)}
              />
            )}
          />
          {errors.inputPrice && (
            <span style={{ color: "red" }}>{errors.inputPrice.message}</span>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InputPriceModal;
